import {
    Alert,
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Typography,
} from "@mui/material";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import { useStyles } from "../MachineCenterCreationForm.styles";
import type { IMachineCenterCreationFormValues } from "../MachineCenterCreationFormInterfaces";

export const QUALITY_TAB_FIELD_NAMES: Array<keyof IMachineCenterCreationFormValues> = ["quality_calculation_method_id"];

interface QualityTabProps {
    values: IMachineCenterCreationFormValues;
    errors: Record<string, unknown>;
    touched: Record<string, unknown>;
    setFieldValue: (field: string, value: unknown) => void;
    t: (key: string) => string;
}

export const QualityTab = ({ values, errors, touched, setFieldValue, t }: QualityTabProps) => {
    const classes = useStyles();
    const { qualityCalculationMethodOptions, loading } = MachineCentersEntity.GetQualityCalculationMethods();

    return (
        <Box className={classes.tabPanel}>
            <Box className={classes.boxContainer}>
                <FormControlLabel
                    className={classes.switchLabel}
                    value="start"
                    control={
                        <Switch
                            className={classes.switch}
                            checked={values.record_quality_enabled}
                            onChange={(e) => {
                                const enabled = e.target.checked;
                                setFieldValue("record_quality_enabled", enabled);
                            }}
                        />
                    }
                    label={t(
                        "corporate_backoffice.production_line_view.machine_center_creation_form.record_quality_enabled",
                    )}
                    labelPlacement="start"
                />
            </Box>

            <Box className={classes.boxContainer}>
                <FormControlLabel
                    className={classes.switchLabel}
                    value="start"
                    disabled={!values.record_quality_enabled}
                    control={
                        <Switch
                            className={classes.switch}
                            checked={values.quality_manual_entry}
                            onChange={(e) => setFieldValue("quality_manual_entry", e.target.checked)}
                        />
                    }
                    label={t(
                        "corporate_backoffice.production_line_view.machine_center_creation_form.quality_manual_entry",
                    )}
                    labelPlacement="start"
                />
            </Box>

            {values.quality_manual_entry && (
                <Box className={classes.boxContainer}>
                    <Alert severity="info">
                        {t(
                            "corporate_backoffice.production_line_view.machine_center_creation_form.quality_entry_interval_info",
                        )}
                    </Alert>
                </Box>
            )}

            <Box className={classes.boxContainer}>
                <InputLabel id="quality-calculation-method-label">
                    <Typography variant="subtitle2" className={classes.formLabel}>
                        {t(
                            "corporate_backoffice.production_line_view.machine_center_creation_form.quality_calculation_method",
                        )}
                    </Typography>
                </InputLabel>
                <FormControl
                    error={Boolean(touched.quality_calculation_method_id && errors.quality_calculation_method_id)}
                    className={classes.textField}
                    fullWidth
                    disabled={loading || !values.record_quality_enabled}
                >
                    <Select
                        labelId="quality-calculation-method-label"
                        id="quality-calculation-method-select"
                        className={classes.select}
                        name="quality_calculation_method_id"
                        value={values.quality_calculation_method_id === "" ? "" : values.quality_calculation_method_id}
                        onChange={(e) =>
                            setFieldValue(
                                "quality_calculation_method_id",
                                e.target.value === "" ? "" : Number(e.target.value),
                            )
                        }
                        displayEmpty
                    >
                        {qualityCalculationMethodOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                                {opt.name}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {touched.quality_calculation_method_id && errors.quality_calculation_method_id
                            ? t(String(errors.quality_calculation_method_id))
                            : null}
                    </FormHelperText>
                </FormControl>
            </Box>
        </Box>
    );
};
